// src/components/profile/ProfileSetup.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiCheck, FiArrowRight, FiHome } from 'react-icons/fi';
import { Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import UserProfileForm from './UserProfileForm';
import OrganizationProfileForm from './OrganizationProfileForm';
import { Link } from 'react-router-dom';
import logo from '../../assets/images/logo.png';

const ProfileSetup = () => {
  const navigate = useNavigate();
  const { userProfile, user, completeUserProfile } = useAuth(); // Make sure to get the function from context
  const [currentStep, setCurrentStep] = useState(1);
  const [userProfileData, setUserProfileData] = useState({});
  const [organizationProfileData, setOrganizationProfileData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = [
    {
      number: 1,
      title: 'Personal Information',
      description: 'Tell us about yourself',
      icon: FiUser
    },
    {
      number: 2,
      title: 'Organization Details',
      description: 'Set up your organization',
      icon: FiHome
    }
  ];

  // Initialize with existing data if available
  useEffect(() => {
    if (userProfile) {
      setUserProfileData({
        displayName: userProfile.displayName || '',
        jobTitle: userProfile.jobTitle || '',
        phone: userProfile.phone || '',
        timezone: userProfile.settings?.preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: userProfile.settings?.preferences?.language || 'en'
      });

      setOrganizationProfileData({
        name: userProfile.organizationName || '',
        industry: userProfile.industry || '',
        website: userProfile.website || '',
        size: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        primaryColor: userProfile.primaryColor || '#2563eb'
      });
    }
  }, [userProfile]);

  const handleUserProfileSubmit = (data) => {
    setUserProfileData(data);
    setCurrentStep(2);
  };

  const handleOrganizationProfileSubmit = async (data) => {
    setOrganizationProfileData(data);
    await completeProfileSetup(data);
  };

  const completeProfileSetup = async (orgData) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Completing profile setup with data:', {
        userProfile: userProfileData,
        organizationProfile: orgData
      });
      
      // Call the AuthContext function to complete profile
      await completeUserProfile({
        userProfile: userProfileData,
        organizationProfile: orgData
      });

      console.log('Profile setup completed successfully');
      // Navigate to dashboard after successful completion
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error completing profile setup:', error);
      setError(error.message || 'Failed to complete profile setup');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-3">
                <img src={logo} alt="TaskRegister" className="h-14 w-auto" />
              </Link>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Profile</h1>
          <p className="text-gray-600">Let's set up your account to get you started</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-center">
            <div className="flex items-center space-x-8">
              {steps.map((step, index) => {
                const isActive = currentStep === step.number;
                const isCompleted = currentStep > step.number;
                const IconComponent = step.icon;

                return (
                  <div key={step.number} className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                        {isCompleted ? (
                          <FiCheck size={20} />
                        ) : (
                          <IconComponent size={20} />
                        )}
                      </div>
                      <div className="mt-2 text-center">
                        <p className={`text-sm font-medium ${
                          isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {step.title}
                        </p>
                        <p className="text-xs text-gray-500">{step.description}</p>
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <FiArrowRight className="text-gray-400 mx-4" size={20} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {currentStep === 1 && (
            <UserProfileForm
              initialData={userProfileData}
              onSubmit={handleUserProfileSubmit}
              onBack={() => setCurrentStep(1)}
              showBackButton={false}
            />
          )}
          
          {currentStep === 2 && (
            <OrganizationProfileForm
              initialData={organizationProfileData}
              onSubmit={handleOrganizationProfileSubmit}
              onBack={() => setCurrentStep(1)}
              showBackButton={true}
              isSetupMode={true}
            />
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex items-center justify-center text-sm text-gray-500">
            <Sparkles className="mr-1" size={16} />
            Powered by AI Technology
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;